import React, { useEffect, useRef, useState, useCallback } from "react";
import { WebSocketConnection } from "../../utils/WebSocketConnection";
import { parseClusterProperties } from "../../utils/clusterPropertyParser";
import {
  AudioMessage_Command,
  AudioMessage_Command_Type,
  AudioMessage_ClusterProperties,
} from "../../generated/audio_message";
import { Bubble } from "./GuiPopupBubbleView";
import GuiPopupAnimatedView from "./GuiPopupBubbleAnimatedView";

export type BubbleStack = {
  id: number;
  type: "bubble_stack";
  duration: number;       // seconds
  fadeDuration: number;   // seconds (ignored for now)
  bubbles: Bubble[];
};

function bubbleFromClusterProperties(
  id: number,
  props: AudioMessage_ClusterProperties
): BubbleStack | undefined {
  const map = parseClusterProperties(props);
  if (map["type"] !== "bubble_stack") return;

  const duration =
    typeof map["duration"] === "number" ? (map["duration"] as number) : 3;
  const fadeDuration =
    typeof map["fade_duration"] === "number"
      ? (map["fade_duration"] as number)
      : 1;

  const rawStack = map["stack"];
  if (!Array.isArray(rawStack)) return;

  let bubbleIdCounter = 0;
  const bubbles: Bubble[] = [];
  for (const item of rawStack) {
    if (
      item &&
      typeof item === "object" &&
      "type" in item &&
      (item as any).type === "bubble"
    ) {
      const color = (item as any).color;
      const text = (item as any).text;
      if (typeof text === "string") {
        bubbles.push({
          id: bubbleIdCounter++,
          bubbleType: "bubble",
          text,
          color,
        });
      }
    }
  }

  if (bubbles.length === 0) return;

  return {
    id,
    type: "bubble_stack",
    duration,
    fadeDuration,
    bubbles,
  };
}

interface GuiPopupBubbleContainerProps {
  connection: WebSocketConnection | null;
}

const GuiPopupBubbleContainer: React.FC<GuiPopupBubbleContainerProps> = ({connection}) => {
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [durationSec, setDurationSec] = useState<number>(0);
  const [stackId, setStackId] = useState<number>(0);
  const [isEnabled, setIsEnabled] = useState<boolean>(true);

  const nextStackIdRef = useRef(0);

  const handleBubblesCommand = useCallback((cmd: AudioMessage_Command) => {
    if (!cmd.payload || cmd.payload.oneofKind !== "clusterProperties") return;

    const props: AudioMessage_ClusterProperties = cmd.payload.clusterProperties;
    const stack = bubbleFromClusterProperties(nextStackIdRef.current++, props);
    if (!stack) return;

    console.log("[GuiPopupBubbleContainer] receive bubble stack:", stack);

    // Update view model: new stack id, bubbles, and duration
    setStackId(stack.id);
    setBubbles(stack.bubbles);
    setDurationSec(stack.duration);
  }, []);

  // When the animated view says the stack elapsed, clear bubbles (let it animate out)
  const handleElapsed = useCallback((elapsedId: number) => {
    // Only clear if the elapsed one is still the current stack
    setBubbles((prev) => {
      // if we're still on that stack, return empty to animate out; otherwise ignore
      return stackId === elapsedId ? [] : prev;
    });
  }, [stackId]);

  // Handle control-b keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for control-b (or cmd-b on Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === 'b') {
        event.preventDefault();
        setIsEnabled(prev => {
          const newState = !prev;
          console.log(`[GuiPopupBubbleContainer] Toggled popup visibility: ${newState ? 'enabled' : 'disabled'}`);
          return newState;
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const conn = connection;
    conn?.addCommandListener(
      "gui-popup-bubble-container",
      AudioMessage_Command_Type.GUI_POPUP,
      handleBubblesCommand
    );

    return () => {
      conn?.removeCommandListener(
        "gui-popup-bubble-container",
        AudioMessage_Command_Type.GUI_POPUP
      );
    };
  }, [handleBubblesCommand, connection]);

  return (isEnabled && bubbles.length > 0) ? (
    <GuiPopupAnimatedView
      stackId={stackId}
      bubbles={bubbles}
      durationSec={durationSec}
      onElapsed={handleElapsed}
    />
  ) : null;
};

export default GuiPopupBubbleContainer;
